import React, { useState, useEffect, useRef } from "react";
import styled, { createGlobalStyle } from "styled-components";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useColumnsContext } from "../context/ColumnsContext";
import { getUserOffers, downloadOfferPdf, downloadLegalCertificate, deleteOffer, downloadOffersExcel, generateOffersExcel, markOfferAsRead } from "../services/api";
import { useCredits } from "../context/CreditsContext";
import { FONTS, COLORS, BREAKPOINTS } from "../utils/styleConstants";
import Sidebar from "../components/Sidebar";
import Footer from "../components/Footer";
import LoadingIndicator from "../components/LoadingIndicator";
import sidebarLogo from "../assets/sidebar_logo.svg";
import { FaCog, FaEye, FaEyeSlash, FaTimes, FaExclamationTriangle, FaTrash, FaSearch } from 'react-icons/fa';
import { formatDateToCustomFormat } from '../utils/dateUtils';

// Define accent colors to match PreviewModal
const ACCENT_ORANGE = "#ff7057";
const DARK_BLUE = "#01174f";
const LIGHT_BLUE = "#a7c3eb";

// Global scrollbar style for modal
const GlobalScrollbarStyle = createGlobalStyle`
  .modal-open {
    overflow: hidden;
  }
  
  /* Estilo base para el área con scroll */
  .modal-scroll-area {
    /* Firefox */
    scrollbar-width: thin;
    scrollbar-color: ${ACCENT_ORANGE} transparent;
    
    /* Eliminar las flechas del scrollbar en todos los navegadores */
    &::-webkit-scrollbar {
      width: 8px;
      height: 8px;
    }
    
    &::-webkit-scrollbar-track {
      background: transparent;
    }
    
    &::-webkit-scrollbar-thumb {
      background-color: ${ACCENT_ORANGE};
      border-radius: 4px;
    }
    
    /* Ocultar botones */
    &::-webkit-scrollbar-button {
      width: 0;
      height: 0;
      display: none;
    }
    
    /* Específicamente ocultar los botones de inicio y fin */
    &::-webkit-scrollbar-button:start:decrement,
    &::-webkit-scrollbar-button:end:increment,
    &::-webkit-scrollbar-button:vertical:start:decrement,
    &::-webkit-scrollbar-button:vertical:end:increment,
    &::-webkit-scrollbar-button:horizontal:start:decrement,
    &::-webkit-scrollbar-button:horizontal:end:increment {
      display: none;
    }
  }
`;

const PageContainer = styled.div`
  width: 100%;
  max-width: 100%;
  margin: 0 auto;
  background-color: var(--secondary-color);
  display: flex;
  box-shadow: var(--box-shadow);
  overflow: hidden;
  height: 100vh;

  @media (max-width: 768px) {
    height: auto;
    min-height: 100vh;
    overflow: visible;
    flex-direction: column;
    box-shadow: none;
  }
`;

const SidebarLogo = styled.img`
  width: 40px;
  height: auto;
  margin-bottom: 2rem;

  @media (max-width: 768px) {
    width: 30px;
    margin-bottom: 0;
    margin-left: 1rem;
  }
`;

const ContentArea = styled.div`
  flex: 1;
  padding: 2rem 2rem;
  border-radius: 1rem 0 0 1rem;
  background: white;
  height: 100%;
  display: flex;
  flex-direction: column;
  overflow-y: auto;
  overflow-x: hidden;

  @media (max-width: 768px) {
    height: auto;
    min-height: calc(100vh - 70px);
    padding: 1.5rem;
    border-radius: 2rem 2rem 0 0;
    overflow-y: auto;
    background: white;
  }
`;

const FormHeader = styled.div`
  margin-bottom: 2rem;
  display: flex;
  flex-direction: column;
  gap: 0.8rem;
  width: 100%;
`;

const HeaderTitle = styled.h1`
  color: ${LIGHT_BLUE};
  font-size: 2.5rem;
  font-weight: 600;
  font-family: "AtHauss", "Avenir Next", sans-serif;
  margin: 0;
  letter-spacing: 0.02em;

  a {
    color: inherit;
    text-decoration: none;
    cursor: pointer;
    
    &:hover {
      text-decoration: underline;
    }
  }

  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

// Header Title row
const HeaderTitleRow = styled.div`
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
`;

// Second row with offer buttons
const OfferButtonsRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  width: 100%;
  margin-top: 0.5rem;
  gap: 1.5rem;
`;

// Credits counter styles
const CreditsCounter = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 0.25rem;

  @media (max-width: 768px) {
    align-items: flex-end;
  }
`;

const CreditsNumber = styled.div`
  font-size: 2rem;
  font-weight: 700;
  color: #ff7057;
  font-family: ${FONTS.PRIMARY};
  text-align: right;

  @media (max-width: 768px) {
    font-size: 1.5rem;
  }
`;

const CreditsLabel = styled.div`
  font-size: 0.7rem;
  color: #ff7057;
  font-family: ${FONTS.SECONDARY};
  font-weight: 500;
  text-align: right;
  white-space: nowrap;

  @media (max-width: 768px) {
    font-size: 0.65rem;
  }
`;

const OfferButtons = styled.div`
  display: flex;
  align-items: center;
  gap: 1.5rem;
`;

const FiltersContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  margin-bottom: 1.5rem;
`;

const FilterRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
  align-items: flex-end;
  margin-top: 1.5rem;
`;

const FilterItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const FilterLabel = styled.label`
  font-size: 0.9rem;
  font-weight: 500;
  color: ${DARK_BLUE};
  font-family: ${FONTS.PRIMARY || 'sans-serif'};
  margin-bottom: 0.2rem;
`;

const FilterSelect = styled.select`
  padding: 0.65rem 1.2rem;
  border-radius: 8px;
  border: 1px solid #e5e7eb;
  background-color: white;
  min-width: 180px;
  font-size: 0.9rem;
  font-family: ${FONTS.SECONDARY || 'sans-serif'};
  color: ${DARK_BLUE};
  appearance: none;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='%23ff7057' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='M6 9l6 6 6-6'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 0.8rem center;
  background-size: 1rem;
  cursor: pointer;
  transition: all 0.2s;

  &:focus {
    outline: none;
    border-color: ${ACCENT_ORANGE};
    box-shadow: 0 0 0 2px rgba(255, 112, 87, 0.1);
  }
  
  &:hover {
    border-color: ${ACCENT_ORANGE};
  }
`;

const SearchContainer = styled.div`
  position: relative;
  flex-shrink: 0;
  width: 200px;
`;

const SearchInput = styled.input`
  padding: 0.4rem 1.8rem 0.4rem 0.8rem;
  border-radius: 4px;
  border: none;
  background-color: #eef1f6;
  width: 100%;
  font-size: 0.85rem;
  font-family: ${FONTS.SECONDARY || 'sans-serif'};
  color: #7fa7db;
  transition: all 0.2s;

  &:focus {
    outline: none;
    box-shadow: 0 0 0 1px rgba(127, 167, 219, 0.2);
  }
  
  &::placeholder {
    color: #7fa7db;
    opacity: 0.8;
  }
`;

const SearchIcon = styled.button`
  position: absolute;
  right: 0.5rem;
  top: 50%;
  transform: translateY(-50%);
  color: #7fa7db;
  opacity: 0.8;
  display: flex;
  align-items: center;
  justify-content: center;
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px;

  &:hover {
    opacity: 1;
  }
`;

const TableContainer = styled.div`
  margin-bottom: 2rem;
  width: 100%;
  max-width: 100%;
  border-radius: 12px;
  background-color: white;
  border: 1px solid #e2e8f0;
  height: 60vh; /* Fixed height */
  display: flex;
  flex-direction: column;
  position: relative; /* Add positioning context */
  padding-bottom: 2px; /* Add slight padding at bottom */
  
  /* Scrollbar personalizado */
  scrollbar-width: thin;
  scrollbar-color: #c1d3f0 transparent;
  
  &::-webkit-scrollbar {
    height: 6px;
    width: 6px;
  }
  
  &::-webkit-scrollbar-thumb {
    background-color: #c1d3f0;
    border-radius: 3px;
  }
  
  &::-webkit-scrollbar-track {
    background-color: transparent;
  }
`;

const TableWrapper = styled.div`
  overflow: auto; /* Enable both horizontal and vertical scrolling */
  flex: 1;
  width: 100%;
  padding-bottom: 15px; /* Increase padding to prevent scrollbar from being cut off */
  box-sizing: border-box;
  
  /* Ensure scrollbar is visible */
  &::-webkit-scrollbar {
    height: 8px;
    width: 8px;
    visibility: visible;
  }
  
  &::-webkit-scrollbar-thumb {
    background-color: #c1d3f0;
    border-radius: 4px;
    visibility: visible;
  }
  
  &::-webkit-scrollbar-track {
    background-color: transparent;
    margin-bottom: 4px;
  }
  
  /* Ensure the bottom of table is visible when scrolling */
  &::after {
    content: '';
    display: block;
    height: 10px; /* Space after the last row */
  }
`;

const Table = styled.table`
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  font-size: 0.9rem;
  table-layout: fixed;
  margin-bottom: 8px; /* Add margin to prevent content from touching scrollbar */
`;

const TableHeader = styled.thead`
  background-color: white;
  position: sticky;
  top: 0;
  z-index: 10;
  
  /* Add shadow effect for better visual separation */
  &::after {
    content: '';
    position: absolute;
    left: 0;
    right: 0;
    bottom: 0;
    height: 1px;
    background-color: #E5E7EB;
  }
`;

const TableHeaderCell = styled.th`
  padding: 0.9rem 1rem;
  text-align: left;
  font-weight: 600;
  color: #556FB5;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  cursor: pointer;
  user-select: none;
  font-size: 0.9rem;
  position: relative;
  
  &::after {
    content: ${props => {
      if (props.className?.includes('sort-asc')) {
        return '"▲"';
      } else if (props.className?.includes('sort-desc')) {
        return '"▼"';
      }
      return '""';
    }};
    font-size: 1rem;
    margin-left: 6px;
    color: #556FB5;
    font-weight: bold;
  }
  
  // Especial para la primera columna (checkbox/radio)
  &:first-child {
    width: 40px;
    padding-right: 0;
  }
`;

const TableBody = styled.tbody`
  overflow-y: auto;
`;

const TableRow = styled.tr`
  background-color: ${props => {
    if (props.$isSelected) return '#EBF2FD';
    if (props.$isUnread) return '#FEFFFE'; // Fondo más blanco para ofertas no leídas
    return props.$index % 2 === 0 ? '#F7FAFF' : 'white';
  }};
  font-weight: ${props => props.$isUnread ? '600' : '400'};
  border-left: ${props => props.$isUnread ? '4px solid #3B82F6' : '4px solid transparent'};

  &:hover {
    background-color: ${props => props.$isSelected ? '#EBF2FD' : '#F0F8FF'};
  }
`;

const TableCell = styled.td`
  padding: 0.85rem 1rem;
  color: #1E293B;
  vertical-align: middle;
  font-size: 0.9rem;
  border-top: 1px solid ${props => props.$index === 0 ? 'transparent' : '#F0F7FF'};
  
  /* Evitar que el contenido se rompa a menos que sea demasiado largo */
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const StatusBadge = styled.span`
  font-weight: ${props => props.$isUnread ? '700' : 'normal'};
`;

const ActionsContainer = styled.div`
  display: flex;
  gap: 0.4rem;
  flex-wrap: nowrap;
`;

const ActionButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.3rem;
  padding: 0.45rem 0.8rem;
  background-color: ${props => props.secondary ? 'white' : props.color || '#ff7057'};
  color: ${props => props.secondary ? (props.color || '#ff7057') : 'white'};
  border: 1px solid ${props => props.color || '#ff7057'};
  border-radius: 1.5rem;
  font-weight: 500;
  font-size: 0.8rem;
  font-family: "WorkSans", "Avenir Next", sans-serif;
  cursor: pointer;
  transition: all 0.2s;
  box-shadow: none;
  text-decoration: none;
  white-space: nowrap;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: ${props => props.secondary ? 
      `0 3px 5px rgba(${props.color ? props.color.replace(/[^\d,]/g, '') : '255, 112, 87'}, 0.15)` : 
      '0 4px 6px rgba(0, 0, 0, 0.1)'};
    background-color: ${props => props.secondary ? '#fffbfa' : props.hoverColor || '#e86548'};
  }
  
  &:active {
    transform: translateY(0);
    box-shadow: none;
  }
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    background-color: ${props => props.secondary ? 'white' : '#e5e7eb'};
    color: ${props => props.secondary ? '#9ca3af' : 'white'};
    border-color: ${props => props.secondary ? '#d1d5db' : '#e5e7eb'};
    box-shadow: none;
    transform: none;
  }
`;

const PaginationContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  margin-top: 2rem; /* Increase margin to provide more space */
  color: #7fa7db;
  padding-bottom: 1rem; /* Add padding to ensure visibility */
`;

const PageInfo = styled.div`
  font-size: 0.9rem;
  color: #7fa7db;
  margin: 0 1rem;
`;

const PaginationButtons = styled.div`
  display: flex;
  gap: 0.5rem;
`;

const PaginationButton = styled.button`
  background-color: ${props => props.active ? "#7fa7db" : 'white'};
  color: ${props => props.active ? 'white' : "#7fa7db"};
  border: 1px solid ${props => props.active ? "#7fa7db" : '#dce1e9'};
  border-radius: 4px;
  padding: 0.5rem 0.8rem;
  font-size: 0.9rem;
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  opacity: ${props => props.disabled ? 0.6 : 1};
  
  &:hover:not(:disabled) {
    background-color: ${props => props.active ? "#7fa7db" : '#f7f9fc'};
  }
`;

const ErrorMessage = styled.div`
  background-color: #fee2e2;
  color: #991b1b;
  padding: 1rem;
  border-radius: 6px;
  margin-bottom: 1.5rem;
`;

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 3rem;
  background-color: #f9fafb;
  border-radius: 8px;
  text-align: center;
`;

const EmptyStateText = styled.p`
  color: ${COLORS.ACCENT};
  font-size: 1.1rem;
  margin-bottom: 1.5rem;
`;

const PrimaryButton = styled(Link)`
  display: inline-block;
  background-color: var(--primary-color);
  color: white;
  padding: 0.7rem 1.8rem;
  border-radius: 1.5rem;
  text-decoration: none;
  font-weight: 600;
  transition: background-color 0.3s;
  
  &:hover {
    background-color: #d67559;
  }
`;

// Modal components
const ModalBackdrop = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1100;
  padding: 0;
`;

const ModalContent = styled.div`
  position: relative;
  background-color: white;
  border-radius: 0.1rem;
  box-shadow: 0 0 20px rgba(0, 0, 0, 0.25);
  width: 100%;
  max-width: 1100px;
  max-height: 90vh;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  outline: none;
  padding-right: 1.5rem;
  
  @media (max-width: ${BREAKPOINTS.MOBILE}) {
    max-height: 80vh;
    margin: 1rem;
  }
`;

const ModalHeader = styled.div`
  display: flex;
  align-items: center;
  padding: 1rem 1.5rem;
  border-bottom: none;
`;

const ModalScrollContent = styled.div`
  overflow-y: auto;
  flex-grow: 1;
  padding-right: 6px;
  
  &::-webkit-scrollbar {
    width: 8px;
    height: 8px;
  }
  
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  
  &::-webkit-scrollbar-thumb {
    background-color: ${ACCENT_ORANGE};
    border-radius: 4px;
  }
  
  &::-webkit-scrollbar-button,
  &::-webkit-scrollbar-button:start:decrement,
  &::-webkit-scrollbar-button:end:increment,
  &::-webkit-scrollbar-button:vertical:start:decrement,
  &::-webkit-scrollbar-button:vertical:end:increment,
  &::-webkit-scrollbar-button:horizontal:start:decrement,
  &::-webkit-scrollbar-button:horizontal:end:increment {
    display: none;
    width: 0;
    height: 0;
  }
`;

const CloseButton = styled.button`
  background: transparent;
  border: 2px solid ${ACCENT_ORANGE};
  width: 32px;
  height: 32px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${ACCENT_ORANGE};
  font-size: 1.6rem;
  cursor: pointer;
  margin-right: 1rem;
  padding: 0;
  line-height: 0.75;
  
  &:hover {
    background-color: #fff0e8;
  }
  
  &:focus {
    outline: none;
  }
`;

const ModalTitle = styled.h2`
  font-size: 1rem;
  font-weight: 400;
  color: ${ACCENT_ORANGE};
  margin: 0;
  font-family: ${FONTS.PRIMARY};
`;

const BrandContainer = styled.div`
  display: flex;
  align-items: center;
  padding: 1rem 1.5rem 1rem 6.5rem;
  border-bottom: none;
`;

const Logo = styled.img`
  height: 24px;
  margin-right: 0.75rem;
  filter: brightness(0.7) contrast(1.4) saturate(1.2);
`;

const BrandName = styled.div`
  font-family: ${FONTS.PRIMARY};
  font-size: 1.5rem;
  font-weight: normal;
  
  span:first-child {
    color: #333;
    font-weight: normal;
  }
  
  span:last-child {
    color: #333;
    font-weight: 800;
  }
`;

const ModalBody = styled.div`
  padding: 1.5rem 1.5rem 1.5rem 6.5rem;
`;

const InfoSection = styled.div`
  margin-bottom: 1rem;
  border-bottom: 1px solid #d5d5d5;
  padding-bottom: 1rem;
  
  &:last-child {
    border-bottom: none;
  }
`;

const SectionTitle = styled.h3`
  font-size: 1rem;
  font-weight: 400;
  color: ${DARK_BLUE};
  margin-bottom: 0.75rem;
  font-family: ${FONTS.PRIMARY};
  letter-spacing: 0.01em;
`;

const InfoGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 0.5rem;
  
  @media (max-width: ${BREAKPOINTS.MOBILE}) {
    grid-template-columns: repeat(2, 1fr);
  }
`;

const InfoItem = styled.div`
  margin-bottom: 0.8rem;
`;

const InfoLabel = styled.div`
  font-size: 0.8rem;
  color: ${COLORS.ACCENT};
  margin-bottom: 0.3rem;
  font-weight: 500;
`;

const InfoValue = styled.div`
  font-size: 0.9rem;
  color: ${DARK_BLUE};
  font-weight: 500;
`;

const ContentSection = styled.div`
  margin-bottom: 1.5rem;
  white-space: pre-wrap;
  font-size: 0.9rem;
  line-height: 1.5;
  font-family: ${FONTS.SECONDARY || 'sans-serif'};
  color: ${DARK_BLUE};
  font-weight: 600;
  
  p {
    margin-bottom: 0.75rem;
  }
  
  ul, ol {
    margin-left: 1.25rem;
    margin-bottom: 0.75rem;
  }
  
  strong {
    font-weight: 800;
  }
`;

const ModalFooter = styled.div`
  padding: 1.5rem 6.5rem;
  border-top: 1px solid #eaecf0;
  display: flex;
  justify-content: flex-end;
  gap: 1rem;
`;

const ModalButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.3rem;
  padding: 0.5rem 1.5rem;
  background-color: ${props => props.secondary ? 'white' : ACCENT_ORANGE};
  color: ${props => props.secondary ? ACCENT_ORANGE : 'white'};
  border: 1px solid ${ACCENT_ORANGE};
  border-radius: 1.5rem;
  font-weight: 500;
  font-size: 0.85rem;
  font-family: ${FONTS.PRIMARY || 'sans-serif'};
  cursor: pointer;
  transition: all 0.2s;
  box-shadow: ${props => props.secondary ? 
    '0 1px 2px rgba(255, 112, 87, 0.1)' : 
    '0 1px 3px rgba(0, 0, 0, 0.1)'};
  white-space: nowrap;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: ${props => props.secondary ? 
      '0 3px 5px rgba(255, 112, 87, 0.15)' : 
      '0 4px 6px rgba(0, 0, 0, 0.1)'};
    background-color: ${props => props.secondary ? '#fffbfa' : '#e86548'};
  }
  
  &:active {
    transform: translateY(0);
    box-shadow: ${props => props.secondary ? 
      '0 1px 2px rgba(255, 112, 87, 0.1)' : 
      '0 1px 3px rgba(0, 0, 0, 0.1)'};
  }
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

// Tabs styles
const TabsContainer = styled.div`
  display: flex;
  gap: 0;
  margin-bottom: 1.5rem;
  padding-bottom: 0.25rem;
  border-bottom: 1px solid #e5e7eb;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  
  /* Hide scrollbars completely */
  &::-webkit-scrollbar {
    display: none;
  }
  
  scrollbar-width: none; /* Firefox */
  -ms-overflow-style: none; /* IE and Edge */
`;

const TabsWrapper = styled.div`
  display: flex;
  flex-wrap: nowrap;
  width: calc(100% - 60px); /* Allow space for the config button and search */
`;

const Tab = styled.button`
  background: none;
  border: none;
  padding: 0.5rem 1rem;
  color: ${props => props.$active ? DARK_BLUE : "#7fa7db"};
  border-bottom: 2px solid ${props => props.$active ? DARK_BLUE : "transparent"};
  font-family: ${FONTS.PRIMARY};
  font-size: 0.9rem;
  cursor: pointer;
  transition: all 0.2s;
  white-space: nowrap;
  position: relative;
  font-weight: ${props => props.$active ? "600" : "400"};
  flex-shrink: 0;
  
  &:hover {
    color: ${DARK_BLUE};
  }
  
  &:not(:last-child)::after {
    content: "";
    position: absolute;
    right: 0;
    top: 30%;
    height: 40%;
    width: 1px;
    background-color: #e5e7eb;
  }
`;

const TabCount = styled.span`
  margin-left: 0.5rem;
  color: ${props => props.$active ? DARK_BLUE : "#7fa7db"};
  font-weight: ${props => props.$active ? "600" : "400"};
  min-width: 1.5rem;
  display: inline-block;
`;

const translateStatus = (status) => {
  switch (status) {
    case "draft": return "Borrador";
    case "sent": return "Enviada";
    case "accepted": return "Aceptada";
    case "rejected": return "Rechazada";
    case "counteroffering": return "Contraoferta";
    case "counteroffered": return "Contraofertada";
    case "expired": return "Expirada";
    case "expiring_in_5_days": return "Vence en 5 días";
    case "finalized": return "Finalizada";
    case "reformulating": return "Reformulando";
    case "reformulated": return "Reformulado";
    case "accepting_reformulation": return "Aceptando reformulación";
    case "rejecting_reformulation": return "Rechazando reformulación";
    case "reformulation_accepted": return "Reformulado aceptado";
    case "reformulation_rejected": return "Reformulado rechazado";
    case "signing": return "Firmando";
    case "partial_signed": return "Firmado parcialmente";
    case "signed": return "Firmado";
    default: return status;
  }
};

// Header buttons container
const HeaderButtons = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 0.7rem;
  min-width: 440px;
  padding-top: 0.2rem;

  @media (max-width: 768px) {
    align-items: flex-start;
    width: 100%;
    min-width: auto;
    padding-top: 0;
  }
`;

// Row for the alternative option text
const AlternativeRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  width: 100%;
`;

// Second row with the button
const OfferButtonRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  width: 100%;
`;

// Download row at the bottom
const DownloadRow = styled.div`
  display: flex;
  justify-content: flex-end;
  width: 100%;
  margin-top: 0.2rem;
  margin-right: 0.15rem;
`;

// Button for new offers
const NewOfferButton = styled(Link)`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0.55rem 1.4rem;
  background-color: white;
  color: ${ACCENT_ORANGE};
  border: 1px solid ${ACCENT_ORANGE};
  border-radius: 2rem;
  font-weight: 400;
  font-size: 0.9rem;
  font-family: ${FONTS.PRIMARY};
  text-decoration: none;
  transition: all 0.2s;
  
  &:hover {
    background-color: ${ACCENT_ORANGE}0D;
  }
`;

// Download button with icon
const DownloadButton = styled.div`
  display: flex;
  align-items: center;
  gap: 0.3rem;
  color: ${props => props.disabled ? '#ccc' : LIGHT_BLUE};
  font-family: ${FONTS.PRIMARY};
  font-size: 0.85rem;
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  white-space: nowrap;
  opacity: ${props => props.disabled ? 0.6 : 1};
  
  &:hover {
    text-decoration: ${props => props.disabled ? 'none' : 'underline'};
  }
`;

const DownloadIcon = styled.div`
  display: inline-block;
  margin-left: 0.3rem;
  position: relative;
  top: 1px;
`;

const DownloadIconSvg = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M3 16.5V18.75C3 19.3467 3.23705 19.919 3.65901 20.341C4.08097 20.7629 4.65326 21 5.25 21H18.75C19.3467 21 19.919 20.7629 20.341 20.341C20.7629 19.919 21 19.3467 21 18.75V16.5M16.5 12L12 16.5M12 16.5L7.5 12M12 16.5V3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

// Add search icon SVG
const SearchIconSVG = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="11" cy="11" r="7" stroke="#7fa7db" strokeWidth="1.5"/>
    <path d="M20 20L16.5 16.5" stroke="#7fa7db" strokeWidth="1.5" strokeLinecap="round"/>
  </svg>
);

// Estilo para los círculos de selección
const RadioCircle = styled.div`
  width: 20px;
  height: 20px;
  border-radius: 50%;
  border: 1.5px solid ${props => props.$selected ? '#556FB5' : '#C4D1E6'};
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  
  &::after {
    content: "";
    width: 12px;
    height: 12px;
    border-radius: 50%;
    background-color: ${props => props.$selected ? '#556FB5' : 'transparent'};
    display: ${props => props.$selected ? 'block' : 'none'};
  }
`;

// Barra de acciones para elementos seleccionados
const ActionsBar = styled.div`
  display: ${props => props.$visible ? 'flex' : 'none'};
  align-items: center;
  justify-content: space-between;
  padding: 0.75rem 1rem;
  background-color: #EBF2FD;
  border-radius: 8px;
  margin-bottom: 1rem;
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 0.75rem;
`;

const SelectionInfo = styled.div`
  color: #556FB5;
  font-size: 0.9rem;
`;

const ActionIconButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  background-color: white;
  color: #556FB5;
  border: 1px solid #C4D1E6;
  border-radius: 1.5rem;
  font-size: 0.85rem;
  cursor: pointer;
  transition: all 0.2s;
  
  &:hover {
    background-color: #F0F7FF;
    transform: translateY(-1px);
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  }
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }
`;

// Añadir estilos para el menú de configuración de columnas
const ColumnsMenu = styled.div`
  position: absolute;
  right: 0;
  top: 100%;
  width: 220px;
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  z-index: 1000; // Aumentar significativamente el z-index
  padding: 1rem;
  display: ${props => props.$open ? 'block' : 'none'};
  max-height: 80vh; // Limitar altura máxima
  overflow-y: auto; // Permitir scroll interno si es necesario
  overflow-x: hidden;
`;

const ColumnsMenuTitle = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.5rem;
  border-bottom: 1px solid #eee;
  padding-bottom: 0.5rem;
  
  h4 {
    margin: 0;
    font-size: 0.9rem;
    font-weight: 600;
    color: ${DARK_BLUE};
  }
  
  button {
    background: none;
    border: none;
    cursor: pointer;
    color: #aaa;
    
    &:hover {
      color: ${DARK_BLUE};
    }
  }
`;

const ColumnToggle = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.5rem 0;
  border-bottom: 1px solid #f5f5f5;
  
  &:last-child {
    border-bottom: none;
  }
  
  label {
    font-size: 0.85rem;
    color: #333;
    cursor: pointer;
  }
`;

const ToggleButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  color: ${props => props.$active ? LIGHT_BLUE : '#ccc'};
  
  &:hover {
    color: ${LIGHT_BLUE};
  }
`;

const ConfigButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  color: ${LIGHT_BLUE};
  display: flex;
  align-items: center;
  position: relative;
`;

// Añadir estilos para los campos adicionales según estado
const AdditionalFields = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  margin-top: 0.3rem;
  font-size: 0.75rem;
  color: #64748b;
  
  span {
    display: flex;
    align-items: center;
    gap: 0.25rem;
  }
`;

const ActionLink = styled.a`
  font-size: 0.75rem;
  color: ${LIGHT_BLUE};
  text-decoration: underline;
  cursor: pointer;
  
  &:hover {
    text-decoration: none;
  }
`;

// Modal de confirmación
const ConfirmModal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1200;
  padding: 0;
`;

const ConfirmModalContent = styled.div`
  position: relative;
  background-color: white;
  border-radius: 0.1rem;
  box-shadow: 0 0 20px rgba(0, 0, 0, 0.25);
  width: 100%;
  max-width: 600px;
  max-height: 90vh;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  outline: none;
  
  @media (max-width: ${BREAKPOINTS.MOBILE}) {
    max-height: 80vh;
    margin: 1rem;
  }
`;

const ConfirmModalHeader = styled.div`
  display: flex;
  align-items: center;
  padding: 1rem 1.5rem;
  border-bottom: none;
`;

const ConfirmModalTitle = styled.h2`
  font-size: 1rem;
  font-weight: 400;
  color: ${ACCENT_ORANGE};
  margin: 0;
  font-family: ${FONTS.PRIMARY};
`;

const ConfirmModalBody = styled.div`
  padding: 1.5rem 1.5rem 1.5rem 6.5rem;
  color: ${DARK_BLUE};
  line-height: 1.6;
  font-family: ${FONTS.SECONDARY || 'sans-serif'};
  font-size: 0.9rem;
  
  p {
    margin: 0;
    
    &:not(:first-child) {
      margin-top: 0.75rem;
    }
  }
`;

const ConfirmModalFooter = styled.div`
  padding: 1.5rem;
  border-top: 1px solid #eaecf0;
  display: flex;
  justify-content: flex-end;
  gap: 1rem;
`;

const ConfirmButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.3rem;
  padding: 0.5rem 1.5rem;
  border-radius: 1.5rem;
  font-weight: 500;
  font-size: 0.85rem;
  font-family: ${FONTS.PRIMARY || 'sans-serif'};
  cursor: pointer;
  transition: all 0.2s;
  white-space: nowrap;
  
  &:hover {
    transform: translateY(-2px);
  }
  
  &:active {
    transform: translateY(0);
  }
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }
`;

const CancelButton = styled(ConfirmButton)`
  background-color: white;
  color: #7fa7db;
  border: 1px solid #e5e7eb;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
  
  &:hover:not(:disabled) {
    background-color: #f9fafb;
    border-color: #7fa7db;
    box-shadow: 0 3px 5px rgba(127, 167, 219, 0.15);
  }
  
  &:active {
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
  }
`;

const DeleteButton = styled(ConfirmButton)`
  background-color: ${ACCENT_ORANGE};
  color: white;
  border: 1px solid ${ACCENT_ORANGE};
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  
  &:hover:not(:disabled) {
    background-color: #e86548;
    box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  }
  
  &:active {
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  }
`;

const OffersListPage = () => {
  const { user, logout } = useAuth();
  const { credits, formatCreditsDisplay } = useCredits();
  const { visibleColumns, toggleColumnVisibility } = useColumnsContext();
  const navigate = useNavigate();
  const location = useLocation();
  const [offers, setOffers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedOfferIds, setSelectedOfferIds] = useState([]);
  
  // Estado para el modal de confirmación de eliminación
  const [deleteConfirmation, setDeleteConfirmation] = useState({
    isOpen: false,
    type: null, // 'single' o 'multiple'
    offerId: null,
    offerIds: [],
    message: ''
  });

  // Estado para sortConfig
  const [sortConfig, setSortConfig] = useState({ key: 'creationDate', direction: 'descending' });
  
  const [columnsMenuOpen, setColumnsMenuOpen] = useState(false);

  const [filters, setFilters] = useState({
    status: "",
    search: ""
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(50); // Default page size 50
  const [totalItems, setTotalItems] = useState(0);
  const [columnsOpen, setColumnsOpen] = useState(false);

  // State for active tab
  const [activeTab, setActiveTab] = useState('todos');

  // State for tab counts
  const [tabCounts, setTabCounts] = useState({
    todos: 0,
    borradores: 0,
    enviados: 0,
    aceptados: 0,
    rechazados: 0,
    contraofertas: 0,
    vencen: 0,
    firmados: 0,
    vencidos: 0
  });

  const [searchValue, setSearchValue] = useState("");
  const [downloading, setDownloading] = useState(false);

  // Efecto para manejar la actualización automática y el tab activo
  useEffect(() => {
    // Detectar parámetros de estado en la URL para activar la pestaña correspondiente
    const urlParams = new URLSearchParams(location.search);
    const statusParam = urlParams.get('status');
    
    if (statusParam) {
      console.log(`🎯 Detectado parámetro de estado en URL: ${statusParam}`);
      
      let targetTab = 'todos';
      let targetFilter = '';
      
      switch(statusParam) {
        case 'accepted':
          targetTab = 'aceptados';
          targetFilter = 'accepted,reformulation_accepted';
          break;
        case 'rejected':
          targetTab = 'rechazados';
          targetFilter = 'rejected,reformulation_rejected';
          break;
        case 'counteroffered':
          targetTab = 'contraofertas';
          targetFilter = 'counteroffered';
          break;
        default:
          targetTab = 'todos';
          targetFilter = '';
      }
      
      console.log(`🎯 Activando pestaña: ${targetTab} con filtro: ${targetFilter}`);
      
      // Solo cambiar si es diferente al tab actual
      if (targetTab !== activeTab) {
        setActiveTab(targetTab);
        setFilters(prev => ({ ...prev, status: targetFilter }));
        setCurrentPage(1);
      }
      
      // Limpiar la URL después de procesar el parámetro para evitar que se mantenga
      const newUrl = window.location.pathname;
      window.history.replaceState({}, '', newUrl);
    }
    
    // Manejar otros estados del location como antes
    if (location.state?.activeTab) {
      handleTabChange(location.state.activeTab);
    }
    if (location.state?.refreshList) {
      fetchOffersWithCounts();
      // Limpiar el estado para evitar actualizaciones innecesarias
      navigate(location.pathname, { replace: true });
    }
  }, [location.search, location.state, activeTab]);

  // Efecto separado para fetchear datos cuando cambia el tab o filtros
  useEffect(() => {
    fetchOffersWithCounts();
  }, [activeTab, filters, currentPage, itemsPerPage, sortConfig]);

  // Función optimizada para obtener ofertas y contadores en una sola llamada
  const fetchOffersWithCounts = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Mapear campos de ordenamiento según la documentación del backend
      const mapSortField = (frontendField) => {
        const fieldMapping = {
          'status': 'status',
          'userReference': 'reference',
          'creationDate': 'offer_creation_date',
          'sentDate': 'offer_sent_date',
          'deadlineToRespond': 'deadline_to_respond',
          'client': 'client',
          'counterparty': 'counterparty',
          'counterpartyLawyer': 'lawyer',
          'lastStatusUpdateDate': 'last_status_update_date'
        };
        return fieldMapping[frontendField] || frontendField;
      };
      
      const params = {
        page: currentPage,
        limit: itemsPerPage,
        search: filters.search || undefined,
        searchFields: "offerNumber,userReference,client,counterparty,lawyer,lawyer.firstName,lawyer.lastName,lawyer.lastNames,lawyer.name,lawyer.fullName",
        sortBy: mapSortField(sortConfig.key),
        sortOrder: sortConfig.direction === 'ascending' ? 'asc' : 'desc',
        includeCounts: true,
        includeExpiring: true
      };
      
      if (filters.status === 'expiring_soon') {
        params.status = 'expiring_in_5_days';
      } else {
        params.status = filters.status || undefined;
      }
      
      console.log("🔄 Fetching offers with params:", params);
      
      const response = await getUserOffers(params);
      console.log("✅ Response received:", response);
      
      setOffers(response.offers || []);
      setTotalItems(response.totalItems || 0);
      
      if (response.statusCounts) {
        setTabCounts({
          todos: response.statusCounts.all || 0,
          borradores: response.statusCounts.draft || 0,
          enviados: response.statusCounts.sent || 0,
          aceptados: response.statusCounts.accepted || 0,
          rechazados: response.statusCounts.rejected || 0,
          contraofertas: response.statusCounts.counteroffered || 0,
          vencen: response.statusCounts.expiring_in_5_days || 0,
          firmados: response.statusCounts.signed || 0,
          vencidos: response.statusCounts.expired || 0
        });
      }
      
      console.log("📊 Updated tab counts:", tabCounts);
      
    } catch (error) {
      console.error("❌ Error fetching offers:", error);
      setError(`Error al cargar las ofertas: ${error.message}`);
      
      setOffers([]);
      setTotalItems(0);
    } finally {
      setLoading(false);
    }
  };

  // Eliminar la función fetchAllCounts anterior ya que ahora está integrada
  
  // Función separada para solo actualizar contadores (útil después de eliminar ofertas)
  const refreshCounts = async () => {
    try {
      const response = await getUserOffers({ 
        limit: 0, 
        includeCounts: true,
        includeExpiring: true 
      });
      
      if (response.statusCounts) {
        setTabCounts({
          todos: response.statusCounts.all || 0,
          borradores: response.statusCounts.draft || 0,
          enviados: response.statusCounts.sent || 0,
          aceptados: response.statusCounts.accepted || 0,
          rechazados: response.statusCounts.rejected || 0,
          contraofertas: response.statusCounts.counteroffered || 0,
          vencen: response.statusCounts.expiring_in_5_days || 0,
          firmados: response.statusCounts.signed || 0,
          vencidos: response.statusCounts.expired || 0
        });
      }
    } catch (error) {
      console.error("Error refreshing counts:", error);
    }
  };



  const handleStatusFilterChange = (e) => {
    setFilters(prev => ({
      ...prev,
      status: e.target.value
    }));
    setCurrentPage(1);
  };

  const handleSearch = () => {
    setFilters(prev => ({
      ...prev,
      search: searchValue
    }));
    setCurrentPage(1);
  };

  const handleSearchInputChange = (e) => {
    setSearchValue(e.target.value);
  };

  const handleSearchKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleViewDetails = async (offer) => {
    // Si la oferta está marcada como no leída, marcarla como leída
    if (offer.is_unread) {
      try {
        await markOfferAsRead(offer.id);
        // Actualizar el estado local para reflejar el cambio inmediatamente
        setOffers(prevOffers => 
          prevOffers.map(o => 
            o.id === offer.id ? { ...o, is_unread: false } : o
          )
        );
      } catch (error) {
        console.error("Error marking offer as read:", error);
        // Continuar con la navegación aunque falle marcar como leída
      }
    }
    
    navigate(`/detalle-oferta/${offer.id}`);
  };

  const handleDownloadCertificate = async (offerId) => {
    try {
      await downloadLegalCertificate(offerId);
    } catch (error) {
      console.error("Error downloading certificate:", error);
      console.error(`Error al descargar el certificado: ${error.message}`);
    }
  };

  const handleEdit = (offerId) => {
    navigate(`/editar-oferta/${offerId}`);
  };

  const handleDeleteSelected = () => {
    // Verificar que hay ofertas seleccionadas
    if (selectedOfferIds.length === 0) {
      console.warn("No hay ofertas seleccionadas para eliminar.");
      return;
    }

    // Filtrar las ofertas que pueden ser eliminadas
    const deletableOffers = selectedOfferIds.filter(id => {
      const offer = offers.find(o => o.id === id);
      return offer && (offer.status === 'draft' || offer.envio_fallido === true);
    });

    if (deletableOffers.length === 0) {
      console.warn("Las ofertas seleccionadas no pueden ser eliminadas. Solo se pueden eliminar ofertas en estado 'Borrador' o con 'Error de envío'.");
      return;
    }

    const nonDeletableCount = selectedOfferIds.length - deletableOffers.length;
    let message = `¿Estás seguro de que quieres eliminar ${deletableOffers.length} oferta${deletableOffers.length !== 1 ? 's' : ''}?`;
    
    if (nonDeletableCount > 0) {
      message += `\n\nNota: ${nonDeletableCount} oferta${nonDeletableCount !== 1 ? 's' : ''} no ${nonDeletableCount !== 1 ? 'serán eliminadas' : 'será eliminada'} porque no ${nonDeletableCount !== 1 ? 'están' : 'está'} en estado 'Borrador' o con 'Error de envío'.`;
    }

    setDeleteConfirmation({
      isOpen: true,
      type: 'multiple',
      offerId: null,
      offerIds: deletableOffers,
      message: message
    });
  };

  // Función para confirmar la eliminación
  const confirmDelete = () => {
    setLoading(true);
    
    if (deleteConfirmation.type === 'single') {
      // Eliminar una sola oferta
      deleteOffer(deleteConfirmation.offerId)
        .then(() => {
          fetchOffersWithCounts();
          // Si la oferta eliminada estaba seleccionada, quitarla de la selección
          setSelectedOfferIds(prev => prev.filter(id => id !== deleteConfirmation.offerId));
        })
        .catch(error => {
          console.error("Error al eliminar oferta:", error);
          console.error(`Error al eliminar la oferta: ${error.message || 'Error desconocido'}`);
        })
        .finally(() => {
          setLoading(false);
          setDeleteConfirmation({ isOpen: false, type: null, offerId: null, offerIds: [], message: '' });
        });
    } else if (deleteConfirmation.type === 'multiple') {
      // Eliminar múltiples ofertas
      Promise.all(deleteConfirmation.offerIds.map(id => 
        deleteOffer(id).catch(error => {
          // Capturar errores individuales para cada oferta
          console.error(`Error al eliminar oferta ${id}:`, error);
          return { id, error: error.message || 'Error desconocido' };
        })
      ))
      .then(results => {
        // Contar éxitos y errores
        const errors = results.filter(r => r && r.error);
        const successCount = deleteConfirmation.offerIds.length - errors.length;
        
        if (errors.length > 0) {
          let errorMessage = `Se eliminaron ${successCount} oferta${successCount !== 1 ? 's' : ''} correctamente.`;
          errorMessage += `\n\nErrores al eliminar ${errors.length} oferta${errors.length !== 1 ? 's' : ''}:`;
          errors.forEach(err => {
            errorMessage += `\n- Oferta ${err.id}: ${err.error}`;
          });
          console.error(errorMessage);
        }
        
        // Actualizar la lista y limpiar selección
        fetchOffersWithCounts();
        setSelectedOfferIds([]);
      })
      .catch(err => {
        console.error("Error general al eliminar ofertas:", err);
        console.error("Error al eliminar las ofertas. Por favor, intenta de nuevo.");
      })
      .finally(() => {
        setLoading(false);
        setDeleteConfirmation({ isOpen: false, type: null, offerId: null, offerIds: [], message: '' });
      });
    }
  };

  // Función para cancelar la eliminación
  const cancelDelete = () => {
    setDeleteConfirmation({ isOpen: false, type: null, offerId: null, offerIds: [], message: '' });
  };

  // Modificar la función handleTabChange para corregir los estados
  const handleTabChange = (tab) => {
    setActiveTab(tab);
    
    // Limpiar filtros existentes antes de aplicar nuevos
    let newFilters = { ...filters, search: filters.search };
    
    // Actualizar filtros basados en el tab
    switch(tab) {
      case 'todos':
        newFilters.status = '';
        break;
      case 'borradores':
        newFilters.status = 'draft';
        break;
      case 'enviados':
        // Solo mostrar estado 'sent' en la pestaña de enviados
        newFilters.status = 'sent';
        break;
      case 'aceptados':
        // Incluir reformulación aceptada en "Aceptados"
        newFilters.status = 'accepted,reformulation_accepted';
        break;
      case 'rechazados':
        // Incluir reformulación rechazada en "Rechazados"
        newFilters.status = 'rejected,reformulation_rejected';
        break;
      case 'contraofertas':
        // Usar counteroffered en lugar de counteroffering
        newFilters.status = 'counteroffered';
        break;
      case 'vencen':
        // Este filtro se manejará especialmente en fetchOffersWithCounts
        newFilters.status = 'expiring_soon';
        break;
      case 'firmados':
        newFilters.status = 'partial_signed,signed';
        break;
      case 'vencidos':
        newFilters.status = 'expired';
        break;
      default:
        newFilters.status = '';
    }
    
    // Actualizar el filtro
    setFilters(newFilters);
    setCurrentPage(1);
  };

  const handleDownloadList = async () => {
    try {
      console.log("🔄 Iniciando descarga de listado de ofertas...");
      console.log("📊 Estado actual:", { filters, sortConfig, activeTab });
      
      // Mostrar indicador de carga de descarga
      setDownloading(true);
      setError(null);
      
      // Mapear campos de ordenamiento según la documentación del backend
      const mapSortField = (frontendField) => {
        const fieldMapping = {
          'status': 'status',
          'userReference': 'reference',
          'creationDate': 'offer_creation_date',
          'sentDate': 'offer_sent_date',
          'expirationDate': 'deadline_to_respond',
          'client': 'client',
          'counterparty': 'counterparty',
          'lawyer': 'lawyer',
          'lastStatusUpdateDate': 'last_status_update_date'
        };
        return fieldMapping[frontendField] || frontendField;
      };
      
      // Preparar parámetros para la descarga
      const downloadParams = {
        search: filters.search || undefined,
        searchFields: "offerNumber,userReference,client,counterparty,lawyer,lawyer.firstName,lawyer.lastName,lawyer.lastNames,lawyer.name,lawyer.fullName",
        sortBy: mapSortField(sortConfig.key),
        sortOrder: sortConfig.direction === 'ascending' ? 'asc' : 'desc',
        limit: 0, // Sin límite para obtener todas las ofertas
        format: 'excel'
      };
      
      // Aplicar filtros de estado según el tab activo
      if (filters.status === 'expiring_soon') {
        downloadParams.status = 'expiring_in_5_days';
      } else {
        downloadParams.status = filters.status || undefined;
      }
      
      console.log("📋 Parámetros de descarga:", downloadParams);
      console.log("🔗 URL que se generará:", `/offers/download?${new URLSearchParams(downloadParams).toString()}`);
      
      // Intentar primero con el endpoint del backend
      try {
        console.log("🚀 Intentando descarga desde backend...");
        await downloadOffersExcel(downloadParams);
        console.log("✅ Descarga completada desde el backend");
      } catch (backendError) {
        console.warn("⚠️ Error con endpoint del backend, generando Excel en frontend:", backendError);
        console.log("🔧 Detalles del error:", {
          status: backendError.response?.status,
          message: backendError.response?.data?.message || backendError.message,
          headers: backendError.response?.headers
        });
        
        // Si falla el backend, obtener todas las ofertas y generar Excel en frontend
        const allOffersParams = {
          ...downloadParams,
          getAllOfStatus: true,
          includeCounts: false,
          includeExpiring: false
        };

        
        
        console.log("📥 Obteniendo ofertas para generación en frontend...");
        const response = await getUserOffers(allOffersParams);
        const allOffers = response.offers || [];
        
        if (allOffers.length === 0) {
          throw new Error("No hay ofertas para descargar con los filtros seleccionados");
        }
        
        console.log(`📊 Generando Excel con ${allOffers.length} ofertas`);
        await generateOffersExcel(allOffers, filters);
        console.log("✅ Excel generado en frontend");
      }
      
    } catch (error) {
      console.error("❌ Error en descarga:", error);
      setError(`Error al descargar el listado: ${error.message}`);
    } finally {
      setDownloading(false);
    }
  };

  const totalPages = Math.ceil(totalItems / itemsPerPage);

  useEffect(() => {
    document.title = "Mis expedientes | Ofertas Vinculantes";
    return () => {
      document.title = "Ofertas Vinculantes";
    };
  }, []);

  // Function to handle sorting
  const requestSort = (key) => {
    let direction = 'ascending';
    if (sortConfig.key === key && sortConfig.direction === 'ascending') {
      direction = 'descending';
    }
    setSortConfig({ key, direction });
  };

  // Helper function to get a className for sorting
  const getSortClassName = (name) => {
    if (!sortConfig) {
      return '';
    }
    // Ensure the returned string matches the CSS classes: 'sort-asc' or 'sort-desc'
    return sortConfig.key === name ? (sortConfig.direction === 'ascending' ? 'sort-asc' : 'sort-desc') : '';
  };

  // Añadir función para manejar la selección
  const handleSelectOffer = (offerId, event) => {
    event.stopPropagation(); // Evitar que se active el handleViewDetails
    
    setSelectedOfferIds(prev => {
      // Si el ID ya está seleccionado, lo quitamos (funcionamiento como checkbox)
      if (prev.includes(offerId)) {
        return prev.filter(id => id !== offerId);
      } 
      // Si no está seleccionado, lo agregamos
      else {
        return [...prev, offerId];
      }
    });
  };
  
  // Función para seleccionar/deseleccionar todos
  const handleSelectAll = () => {
    if (selectedOfferIds.length === offers.length) {
      setSelectedOfferIds([]);
    } else {
      setSelectedOfferIds(offers.map(offer => offer.id));
    }
  };
  
  // Función para eliminar una sola oferta
  const handleDeleteSingle = (offerId) => {
    const offer = offers.find(o => o.id === offerId);
    
    if (!offer) {
      console.warn("No se encontró la oferta.");
      return;
    }
    
    // Verificar si la oferta puede ser eliminada
    if (offer.status !== 'draft' && !offer.envio_fallido) {
      console.warn("Esta oferta no puede ser eliminada. Solo se pueden eliminar ofertas en estado 'Borrador' o con 'Error de envío'.");
      return;
    }
    
    const referencia = offer.referencia_expediente || offer.reference || offer.user_reference || 'Sin referencia';
    const estado = offer.status === 'draft' ? 'Borrador' : 'Error de envío';
    
    setDeleteConfirmation({
      isOpen: true,
      type: 'single',
      offerId: offerId,
      offerIds: [],
      message: `¿Estás seguro de que quieres eliminar esta oferta?\n\nReferencia: ${referencia}\nEstado: ${estado}`
    });
  };

  let sortedOffers = offers;
  if (sortConfig.key === 'deadlineToRespond') {
    sortedOffers = [...offers].sort((a, b) => {
      const aRaw = a.fecha_vencimiento || a.expirationDate || a.deadlineToRespond || '';
      const bRaw = b.fecha_vencimiento || b.expirationDate || b.deadlineToRespond || '';
      if (!aRaw && !bRaw) return 0;
      if (!aRaw) return 1;
      if (!bRaw) return -1;
      const aDate = new Date(aRaw);
      const bDate = new Date(bRaw);
      if (isNaN(aDate) && isNaN(bDate)) return 0;
      if (isNaN(aDate)) return 1;
      if (isNaN(bDate)) return -1;
      if (sortConfig.direction === 'ascending') {
        return aDate - bDate;
      } else {
        return bDate - aDate;
      }
    });
  }

  return (
    <PageContainer>
      <Sidebar />
      <ContentArea>
        <FormHeader>
          <HeaderTitleRow>
            <HeaderTitle>
              <Link to="/offers">Mis expedientes</Link>
            </HeaderTitle>
            <CreditsCounter>
              <CreditsNumber>{formatCreditsDisplay().number}</CreditsNumber>
              <CreditsLabel>{formatCreditsDisplay().label}</CreditsLabel>
            </CreditsCounter>
          </HeaderTitleRow>
          
          <OfferButtonsRow>
            <OfferButtons>
              <NewOfferButton to="/nueva-oferta">
                Nueva Oferta Vinculante
              </NewOfferButton>
            </OfferButtons>
          </OfferButtonsRow>
          
          <DownloadRow>
            <DownloadButton 
              onClick={handleDownloadList}
              disabled={downloading}
            >
              {downloading ? 'Descargando...' : 'Descargar listado'}
              <DownloadIcon>
                <DownloadIconSvg />
              </DownloadIcon>
            </DownloadButton>
          </DownloadRow>
        </FormHeader>

        {error && <ErrorMessage>{error}</ErrorMessage>}

        {/* Tabs */}
        <TabsContainer>
          <TabsWrapper>
            <Tab $active={activeTab === 'todos'} onClick={() => handleTabChange('todos')}>
              Todos <TabCount $active={activeTab === 'todos'}>{tabCounts.todos || 0}</TabCount>
            </Tab>
            <Tab $active={activeTab === 'borradores'} onClick={() => handleTabChange('borradores')}>
              Borradores <TabCount $active={activeTab === 'borradores'}>{tabCounts.borradores || 0}</TabCount>
            </Tab>
            <Tab $active={activeTab === 'enviados'} onClick={() => handleTabChange('enviados')}>
              Enviados <TabCount $active={activeTab === 'enviados'}>{tabCounts.enviados || 0}</TabCount>
            </Tab>
            <Tab $active={activeTab === 'aceptados'} onClick={() => handleTabChange('aceptados')}>
              Aceptados <TabCount $active={activeTab === 'aceptados'}>{tabCounts.aceptados || 0}</TabCount>
            </Tab>
            <Tab $active={activeTab === 'rechazados'} onClick={() => handleTabChange('rechazados')}>
              Rechazados <TabCount $active={activeTab === 'rechazados'}>{tabCounts.rechazados || 0}</TabCount>
            </Tab>
            <Tab $active={activeTab === 'contraofertas'} onClick={() => handleTabChange('contraofertas')}>
              Contraofertas <TabCount $active={activeTab === 'contraofertas'}>{tabCounts.contraofertas || 0}</TabCount>
            </Tab>
            <Tab $active={activeTab === 'vencen'} onClick={() => handleTabChange('vencen')}>
              Vencen en 5 días <TabCount $active={activeTab === 'vencen'}>{tabCounts.vencen || 0}</TabCount>
            </Tab>
            <Tab $active={activeTab === 'firmados'} onClick={() => handleTabChange('firmados')}>
              Firmados <TabCount $active={activeTab === 'firmados'}>{tabCounts.firmados || 0}</TabCount>
            </Tab>
            <Tab $active={activeTab === 'vencidos'} onClick={() => handleTabChange('vencidos')}>
              Vencidos <TabCount $active={activeTab === 'vencidos'}>{tabCounts.vencidos || 0}</TabCount>
            </Tab>
          </TabsWrapper>
          
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: '0.5rem',
            position: 'relative' // Asegurar posicionamiento relativo correcto
          }}>
            <ConfigButton onClick={() => setColumnsMenuOpen(!columnsMenuOpen)}>
              <FaCog size={14} />
              <ColumnsMenu $open={columnsMenuOpen}>
                <ColumnsMenuTitle>
                  <h4>Configurar columnas</h4>
                  <button onClick={() => setColumnsMenuOpen(false)}>
                    <FaTimes size={14} />
                  </button>
                </ColumnsMenuTitle>
                
                <ColumnToggle>
                  <label>Selector</label>
                  <ToggleButton 
                    $active={visibleColumns.selector} 
                    onClick={() => toggleColumnVisibility('selector')}
                    disabled={visibleColumns.selector}
                  >
                    {visibleColumns.selector ? <FaEye size={14} /> : <FaEyeSlash size={14} />}
                  </ToggleButton>
                </ColumnToggle>
                
                <ColumnToggle>
                  <label>Estado</label>
                  <ToggleButton 
                    $active={visibleColumns.status} 
                    onClick={() => toggleColumnVisibility('status')}
                  >
                    {visibleColumns.status ? <FaEye size={14} /> : <FaEyeSlash size={14} />}
                  </ToggleButton>
                </ColumnToggle>
                
                <ColumnToggle>
                  <label>Referencia (Obligatorio)</label>
                  <ToggleButton 
                    $active={true} 
                    disabled={true}
                  >
                    <FaEye size={14} />
                  </ToggleButton>
                </ColumnToggle>
                
                <ColumnToggle>
                  <label>Fecha creación (Obligatorio)</label>
                  <ToggleButton 
                    $active={true} 
                    disabled={true}
                  >
                    <FaEye size={14} />
                  </ToggleButton>
                </ColumnToggle>
                
                <ColumnToggle>
                  <label>Fecha envío</label>
                  <ToggleButton 
                    $active={visibleColumns.sendDate} 
                    onClick={() => toggleColumnVisibility('sendDate')}
                  >
                    {visibleColumns.sendDate ? <FaEye size={14} /> : <FaEyeSlash size={14} />}
                  </ToggleButton>
                </ColumnToggle>
                
                <ColumnToggle>
                  <label>Fecha vencimiento</label>
                  <ToggleButton 
                    $active={visibleColumns.expirationDate} 
                    onClick={() => toggleColumnVisibility('expirationDate')}
                  >
                    {visibleColumns.expirationDate ? <FaEye size={14} /> : <FaEyeSlash size={14} />}
                  </ToggleButton>
                </ColumnToggle>
                
                <ColumnToggle>
                  <label>Cliente</label>
                  <ToggleButton 
                    $active={visibleColumns.client} 
                    onClick={() => toggleColumnVisibility('client')}
                  >
                    {visibleColumns.client ? <FaEye size={14} /> : <FaEyeSlash size={14} />}
                  </ToggleButton>
                </ColumnToggle>
                
                <ColumnToggle>
                  <label>Contraparte</label>
                  <ToggleButton 
                    $active={visibleColumns.counterparty} 
                    onClick={() => toggleColumnVisibility('counterparty')}
                  >
                    {visibleColumns.counterparty ? <FaEye size={14} /> : <FaEyeSlash size={14} />}
                  </ToggleButton>
                </ColumnToggle>
                
                <ColumnToggle>
                  <label>Abogado</label>
                  <ToggleButton 
                    $active={visibleColumns.lawyer} 
                    onClick={() => toggleColumnVisibility('lawyer')}
                  >
                    {visibleColumns.lawyer ? <FaEye size={14} /> : <FaEyeSlash size={14} />}
                  </ToggleButton>
                </ColumnToggle>
              </ColumnsMenu>
            </ConfigButton>
            
            <SearchContainer>
              <SearchInput
                type="text"
                placeholder="Buscar..."
                value={searchValue}
                onChange={handleSearchInputChange}
                onKeyPress={handleSearchKeyPress}
              />
              <SearchIcon onClick={handleSearch}>
                <FaSearch />
              </SearchIcon>
            </SearchContainer>
          </div>
        </TabsContainer>

        {loading ? (
          <LoadingIndicator />
        ) : offers.length === 0 ? (
          <EmptyState>
            <EmptyStateText>No se encontraron ofertas con los filtros seleccionados.</EmptyStateText>
            <PrimaryButton as={Link} to="/nueva-oferta">
              Crear Nueva Oferta
            </PrimaryButton>
          </EmptyState>
        ) : (
          <>
            <ActionsBar $visible={selectedOfferIds.length > 0}>
              <SelectionInfo>
                {selectedOfferIds.length} elemento{selectedOfferIds.length !== 1 ? 's' : ''} seleccionado{selectedOfferIds.length !== 1 ? 's' : ''}
              </SelectionInfo>
              <ActionButtons>
                <ActionIconButton 
                  onClick={() => {
                    if (selectedOfferIds.length === 1) {
                      navigate(`/detalle-oferta/${selectedOfferIds[0]}`);
                    }
                  }}
                  disabled={selectedOfferIds.length !== 1}
                >
                  <span>Ver detalle</span>
                </ActionIconButton>
                <ActionIconButton 
                  onClick={() => navigate(`/editar-oferta/${selectedOfferIds[0]}`)}
                  disabled={selectedOfferIds.length !== 1 || offers.find(o => o.id === selectedOfferIds[0])?.status !== 'draft'}
                >
                  <span>Editar</span>
                </ActionIconButton>
                <ActionIconButton 
                  onClick={handleDeleteSelected}
                  disabled={selectedOfferIds.length === 0 || !selectedOfferIds.some(id => {
                    const offer = offers.find(o => o.id === id);
                    return offer?.status === 'draft' || offer?.envio_fallido;
                  })}
                  title={
                    selectedOfferIds.length === 0 
                      ? "Selecciona al menos una oferta" 
                      : !selectedOfferIds.some(id => {
                          const offer = offers.find(o => o.id === id);
                          return offer?.status === 'draft' || offer?.envio_fallido;
                        })
                        ? "Solo se pueden eliminar ofertas en estado 'Borrador' o con 'Error de envío'"
                        : "Eliminar ofertas seleccionadas"
                  }
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.3rem'
                  }}
                >
                  <FaTrash size={14} />
                  <span>Eliminar</span>
                </ActionIconButton>
              </ActionButtons>
            </ActionsBar>

            <TableContainer>
              <TableWrapper>
              <Table>
                <TableHeader>
                  <tr>
                      {visibleColumns.selector && (
                        <TableHeaderCell style={{padding: '0.9rem 0.5rem 0.9rem 1rem'}}>
                          <RadioCircle 
                            $selected={selectedOfferIds.length === offers.length && offers.length > 0} 
                            onClick={handleSelectAll}
                          />
                        </TableHeaderCell>
                      )}
                      
                      {visibleColumns.status && (
                        <TableHeaderCell onClick={() => requestSort('status')} className={getSortClassName('status')}>
                          Estado
                        </TableHeaderCell>
                      )}
                      
                      {visibleColumns.reference && (
                        <TableHeaderCell onClick={() => requestSort('userReference')} className={getSortClassName('userReference')}>
                          Ref. propia
                        </TableHeaderCell>
                      )}
                      
                      {visibleColumns.creationDate && (
                        <TableHeaderCell onClick={() => requestSort('creationDate')} className={getSortClassName('creationDate')}>
                          Fecha creación
                        </TableHeaderCell>
                      )}
                      
                      {visibleColumns.sendDate && (
                        <TableHeaderCell onClick={() => requestSort('sentDate')} className={getSortClassName('sentDate')}>
                          Fecha envío
                        </TableHeaderCell>
                      )}
                      
                      {visibleColumns.expirationDate && (
                        <TableHeaderCell onClick={() => requestSort('deadlineToRespond')} className={getSortClassName('deadlineToRespond')}>
                          Fecha vencimiento
                        </TableHeaderCell>
                      )}
                      
                      {visibleColumns.client && (
                        <TableHeaderCell onClick={() => requestSort('client')} className={getSortClassName('client')}>
                          Cliente
                        </TableHeaderCell>
                      )}
                      
                      {visibleColumns.counterparty && (
                        <TableHeaderCell onClick={() => requestSort('counterparty')} className={getSortClassName('counterparty')}>
                          Contraparte
                        </TableHeaderCell>
                      )}
                      
                      {visibleColumns.lawyer && (
                        <TableHeaderCell onClick={() => requestSort('counterpartyLawyer')} className={getSortClassName('counterpartyLawyer')}>
                          Abogado
                        </TableHeaderCell>
                      )}
                  </tr>
                </TableHeader>
                <TableBody>
                    {sortedOffers
                      .filter(offer => {
                        // Ya no ocultamos los estados temporales
                        return true;
                      })
                      .map((offer, index) => {
                      const isSelected = selectedOfferIds.includes(offer.id);
                      const isUnread = offer.is_unread || false;
                      
                      // Variables para los campos según el estado
                      let abogado = '-';
                      let errorEnvio = offer.envio_fallido || false;
                      
                      // Obtener datos del abogado si existe
                      if (offer.lawyer) {
                        if (typeof offer.lawyer === 'object') {
                          const firstName = offer.lawyer.firstName || offer.lawyer.name || '';
                          const lastName = offer.lawyer.lastNames || offer.lawyer.lastName || '';
                          abogado = `${firstName} ${lastName}`.trim() || '-';
                        } else if (typeof offer.lawyer === 'string') {
                          abogado = offer.lawyer;
                        }
                      }
                      
                      // Mapeo correcto de propiedades del objeto offer
                      const status = offer.status || '-';
                      const referencia = offer.referencia_expediente || offer.reference || offer.user_reference || '-';
                      const fechaCreacion = offer.fecha_creacion || offer.creationDate || '-';
                      const fechaEnvio = offer.fecha_envio || offer.sentDate || '-';
                      const fechaVencimiento = offer.fecha_vencimiento || offer.expirationDate || offer.deadlineToRespond || '-';
                      
                      // Asegurarse de que cliente y contraparte sean strings
                      let cliente = '-';
                      if (offer.cliente_nombre) {
                        cliente = offer.cliente_nombre;
                      } else if (offer.client_name) {
                        cliente = offer.client_name;
                      } else if (offer.client) {
                        // Si client es un objeto, extraer sus propiedades correctamente
                        if (typeof offer.client === 'object' && offer.client !== null) {
                          const firstName = offer.client.firstName || '';
                          const lastName = offer.client.lastNames || offer.client.lastName || '';
                          const fullName = `${firstName} ${lastName}`.trim();
                          cliente = fullName || '-';
                        } else if (typeof offer.client === 'string') {
                          cliente = offer.client || '-';
                        }
                      } else if (offer.offeror) {
                        // Si no hay datos de cliente, usar los datos del ofertante (parte representada)
                        if (typeof offer.offeror === 'object' && offer.offeror !== null) {
                          const firstName = offer.offeror.firstName || '';
                          const lastName = offer.offeror.lastNames || offer.offeror.lastName || '';
                          const fullName = `${firstName} ${lastName}`.trim();
                          cliente = fullName || '-';
                        } else if (typeof offer.offeror === 'string') {
                          cliente = offer.offeror || '-';
                        }
                      }
                      
                      // Lo mismo para contraparte
                      let contraparte = '-';
                      if (offer.contraparte_nombre) {
                        contraparte = offer.contraparte_nombre;
                      } else if (offer.counterparty_name) {
                        contraparte = offer.counterparty_name;
                      } else if (offer.counterparty) {
                        // Si counterparty es un objeto, extraer sus propiedades correctamente
                        if (typeof offer.counterparty === 'object' && offer.counterparty !== null) {
                          const firstName = offer.counterparty.firstName || '';
                          const lastName = offer.counterparty.lastNames || offer.counterparty.lastName || '';
                          const fullName = `${firstName} ${lastName}`.trim();
                          contraparte = fullName || '-';
                        } else if (typeof offer.counterparty === 'string') {
                          contraparte = offer.counterparty || '-';
                        }
                      }
                      
                      // Formatear fechas correctamente con el nuevo formato
                      const formattedFechaCreacion = 
                        fechaCreacion !== '-' && fechaCreacion 
                          ? formatDateToCustomFormat(fechaCreacion)
                          : fechaCreacion;
                        
                      const formattedFechaEnvio = 
                        fechaEnvio !== '-' && fechaEnvio 
                          ? formatDateToCustomFormat(fechaEnvio)
                          : fechaEnvio;
                        
                      const formattedFechaVencimiento = 
                        fechaVencimiento !== '-' && fechaVencimiento
                          ? formatDateToCustomFormat(fechaVencimiento)
                          : fechaVencimiento;
                      
                      // Mapeo de estados a texto en español
                      const getEstadoText = (status) => {
                        if (status === '-') return '-';
                        switch(status) {
                          case 'draft': return 'Borrador';
                          case 'sent': return 'Enviado';
                          case 'accepted': return 'Aceptado';
                          case 'counteroffering': return 'Contraoferta';
                          case 'counteroffered': return 'Contraofertada';
                          case 'rejected': return 'Rechazado';
                          case 'finalized': return 'Firmado';
                          case 'expired': return 'Vencido';
                          case 'reformulating': return 'Reformulando';
                          case 'reformulated': return 'Reformulado';
                          case 'accepting_reformulation': return 'Aceptando reformulación';
                          case 'rejecting_reformulation': return 'Rechazando reformulación';
                          case 'reformulation_accepted': return 'Reformulado aceptado';
                          case 'reformulation_rejected': return 'Reformulado rechazado';
                          case 'sending': return 'Enviando';
                          case 'accepting': return 'Aceptando';
                          case 'rejecting': return 'Denegando';
                          case 'reformulating': return 'Reformulando';
                          case 'rejecting_reformulating': return 'Denegando reformulación';
                          case 'accepting_reformulating': return 'Aceptando reformulación';
                          case 'signing': return 'Firmando';
                          case 'partial_signed': return 'Firmado parcialmente';
                          case 'signed': return 'Firmado';
                          default: return status;
                        }
                      };
                      
                      return (
                        <TableRow 
                          key={offer.id} 
                            onClick={() => handleViewDetails(offer)}
                          $isSelected={isSelected}
                          $isUnread={isUnread}
                          $index={index}
                        >
                          {visibleColumns.selector && (
                            <TableCell $index={index} style={{padding: '0.85rem 0.5rem 0.85rem 1rem'}}>
                              <RadioCircle 
                                $selected={isSelected} 
                                onClick={(e) => handleSelectOffer(offer.id, e)}
                              />
                            </TableCell>
                          )}
                          
                                                      {visibleColumns.status && (
                            <TableCell $index={index}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '0.3rem' }}>
                                <StatusBadge $isUnread={isUnread}>
                                  {getEstadoText(status)}
                                </StatusBadge>
                                {errorEnvio && (
                                  <div title="Error en el envío. Se requiere volver a crear el caso.">
                                    <FaExclamationTriangle color="#f59e0b" size={14} />
                                  </div>
                                )}
                              </div>
                              
                              {/* Información adicional según estado - Más detallada y estructurada */}
                              {status === 'sent' && errorEnvio && (
                                <AdditionalFields>
                                  {formattedFechaEnvio !== '-' && (
                                    <span>Enviado: {formattedFechaEnvio}</span>
                                  )}
                                  <span style={{color: '#ef4444'}}>Envío fallido</span>
                                </AdditionalFields>
                              )}
                              
                              {status === 'accepted' && (
                                <AdditionalFields>
                                  {/* Información adicional removida según solicitud del usuario */}
                                </AdditionalFields>
                              )}
                              
                              {status === 'rejected' && (
                                <AdditionalFields>
                                  {formattedFechaEnvio !== '-' && (
                                    <span>Enviado: {formattedFechaEnvio}</span>
                                  )}
                                  {offer.fechaRechazo && (
                                    <span>Rechazado: {new Date(offer.fechaRechazo).toLocaleDateString('es-ES', {day: '2-digit', month: '2-digit', year: '2-digit'})}</span>
                                  )}
                                  {offer.canShowLegalCertificateButton === true && (
                                    <ActionLink onClick={(e) => {
                                      e.stopPropagation();
                                      handleDownloadCertificate(offer.id);
                                    }}>
                                      Certificado legal
                                    </ActionLink>
                                  )}
                                </AdditionalFields>
                              )}
                              
                              {status === 'reformulation_rejected' && (
                                <AdditionalFields>
                                  {formattedFechaEnvio !== '-' && (
                                    <span>Enviado: {formattedFechaEnvio}</span>
                                  )}
                                  {offer.fechaRechazo && (
                                    <span>Reformulación rechazada: {new Date(offer.fechaRechazo).toLocaleDateString('es-ES', {day: '2-digit', month: '2-digit', year: '2-digit'})}</span>
                                  )}
                                  {offer.canShowLegalCertificateButton === true && (
                                    <ActionLink onClick={(e) => {
                                      e.stopPropagation();
                                      handleDownloadCertificate(offer.id);
                                    }}>
                                      Certificado legal
                                    </ActionLink>
                                  )}
                                </AdditionalFields>
                              )}
                              
                              {status === 'counteroffering' && (
                                <AdditionalFields>
                                  {formattedFechaEnvio !== '-' && (
                                    <span>Enviado: {formattedFechaEnvio}</span>
                                  )}
                                  {formattedFechaVencimiento !== '-' && (
                                    <span>Vence: {formattedFechaVencimiento}</span>
                                  )}
                                </AdditionalFields>
                              )}
                              
                              {status === 'expiring_in_5_days' && (
                                <AdditionalFields>
                                  {formattedFechaEnvio !== '-' && (
                                    <span>Enviado: {formattedFechaEnvio}</span>
                                  )}
                                  {formattedFechaVencimiento !== '-' && (
                                    <span style={{ color: '#f59e0b' }}>Vence: {formattedFechaVencimiento}</span>
                                  )}
                                </AdditionalFields>
                              )}
                              
                              {status === 'finalized' && (
                                <AdditionalFields>
                                  {formattedFechaEnvio !== '-' && (
                                    <span>Enviado: {formattedFechaEnvio}</span>
                                  )}
                                  <ActionLink onClick={(e) => {
                                    e.stopPropagation();
                                    console.log('Funcionalidad para ver requisitos procedimentales pendiente de implementar');
                                  }}>
                                    Ver requisitos
                                  </ActionLink>
                                </AdditionalFields>
                              )}
                              
                              {status === 'expired' && (
                                <AdditionalFields>
                                  {formattedFechaEnvio !== '-' && (
                                    <span>Enviado: {formattedFechaEnvio}</span>
                                  )}
                                  {formattedFechaVencimiento !== '-' && (
                                    <span style={{ color: '#6b7280' }}>Venció: {formattedFechaVencimiento}</span>
                                  )}
                                  {offer.canShowLegalCertificateButton === true && (
                                  <ActionLink onClick={(e) => {
                                    e.stopPropagation();
                                    handleDownloadCertificate(offer.id);
                                  }}>
                                    Certificado legal
                                  </ActionLink>
                                  )}
                                </AdditionalFields>
                              )}
                      </TableCell>
                          )}
                          
                          {visibleColumns.reference && (
                            <TableCell $index={index}>{referencia}</TableCell>
                          )}
                          
                          {visibleColumns.creationDate && (
                            <TableCell $index={index}>{formattedFechaCreacion}</TableCell>
                          )}
                          
                          {visibleColumns.sendDate && (
                            <TableCell $index={index}>{formattedFechaEnvio}</TableCell>
                          )}
                          
                          {visibleColumns.expirationDate && (
                            <TableCell $index={index}>{formattedFechaVencimiento}</TableCell>
                          )}
                          
                          {visibleColumns.client && (
                            <TableCell $index={index}>{cliente}</TableCell>
                          )}
                          
                          {visibleColumns.counterparty && (
                            <TableCell $index={index}>{contraparte}</TableCell>
                          )}
                          
                          {visibleColumns.lawyer && (
                            <TableCell $index={index}>{abogado}</TableCell>
                          )}
                    </TableRow>
                      );
                    })}
                </TableBody>
              </Table>
              </TableWrapper>
            </TableContainer>

            {totalPages > 1 && (
              <PaginationContainer>
                <PaginationButtons>
                  <PaginationButton 
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                  >
                    ◀
                  </PaginationButton>
                  <PageInfo>
                    {currentPage} de {totalPages}
                  </PageInfo>
                  <PaginationButton 
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                  >
                    ▶
                  </PaginationButton>
                </PaginationButtons>
              </PaginationContainer>
            )}
          </>
        )}

        <Footer />

        {/* Modal de confirmación de eliminación */}
        {deleteConfirmation.isOpen && (
          <ConfirmModal onClick={cancelDelete}>
            <ConfirmModalContent onClick={(e) => e.stopPropagation()}>
              <ConfirmModalHeader>
                <CloseButton onClick={cancelDelete}>×</CloseButton>
                <ConfirmModalTitle>Confirmar eliminación</ConfirmModalTitle>
              </ConfirmModalHeader>
              
              <ConfirmModalBody>
                <div style={{ 
                  display: 'flex', 
                  alignItems: 'flex-start', 
                  gap: '1rem',
                  marginLeft: '-5rem'
                }}>
                  <div style={{ flex: 1 }}>
                    {deleteConfirmation.message.split('\n').map((line, index) => (
                      <p key={index}>
                        {line}
                      </p>
                    ))}
                    <p style={{ fontWeight: 600, marginTop: '1.5rem' }}>
                      Esta acción no se puede deshacer.
                    </p>
                  </div>
                </div>
              </ConfirmModalBody>
              
              <ConfirmModalFooter>
                <CancelButton onClick={cancelDelete} disabled={loading}>
                  Atrás
                </CancelButton>
                <DeleteButton onClick={confirmDelete} disabled={loading}>
                  {loading ? 'Eliminando...' : 'Eliminar'}
                </DeleteButton>
              </ConfirmModalFooter>
            </ConfirmModalContent>
          </ConfirmModal>
        )}
      </ContentArea>
    </PageContainer>
  );
};

export default OffersListPage; 